import { useCallback, useMemo, useState } from 'react';

import { UserRole } from '@shared/types';

import { RELEASE_NOTES } from './generated';
import {
  ReleaseNoteChannel,
  ReleaseNoteEntry,
  RELEASE_NOTE_CHANNEL_ORDER,
  RELEASE_NOTE_ROLE_CHANNELS,
} from './releaseNotes.interface';

const LAST_SEEN_KEY = 'lastSeenReleaseNoteId';

const readLastSeen = (): string | null => {
  try {
    return localStorage.getItem(LAST_SEEN_KEY);
  } catch {
    //* 사파리 프라이빗 모드 등에서 접근이 막히면 "다 읽음"으로 두지 않고 무시
    return null;
  }
};

export const releaseNoteChannelsOfRole = (
  roles: UserRole[] | undefined,
): ReleaseNoteChannel[] => {
  if (!roles || roles.length === 0) return [];
  const channels = new Set<ReleaseNoteChannel>();
  roles.forEach((role) => {
    (RELEASE_NOTE_ROLE_CHANNELS[role] ?? []).forEach((channel) => channels.add(channel));
  });
  return RELEASE_NOTE_CHANNEL_ORDER.filter((channel) => channels.has(channel));
};

/**
 * 주어진 채널의 릴리즈 노트를 릴리즈ID 최신순으로 묶어 준다.
 * 마지막 열람 릴리즈는 localStorage에만 두고 서버로 보내지 않는다 (기기별로 충분).
 */
export const useReleaseNotes = function (channels: ReleaseNoteChannel[]) {
  const [lastSeenReleaseId, setLastSeenReleaseId] = useState<string | null>(readLastSeen);

  const notes = useMemo(
    () =>
      RELEASE_NOTES.filter((note) =>
        channels.includes(note.channel as ReleaseNoteChannel),
      ),
    [channels],
  );

  //* 릴리즈ID 최신순 (파일명이 YYYY-MM-DD라 문자열 비교로 충분)
  const releaseIds = useMemo(() => {
    const ids = Array.from(new Set(notes.map((note) => note.releaseId)));
    return ids.sort((a, b) => b.localeCompare(a));
  }, [notes]);

  const notesByRelease = useMemo(() => {
    const map: Record<string, ReleaseNoteEntry[]> = {};
    notes.forEach((note) => {
      const bucket = map[note.releaseId];
      if (bucket) bucket.push(note);
      else map[note.releaseId] = [note];
    });
    return map;
  }, [notes]);

  const latestReleaseId = releaseIds[0] ?? null;

  const hasUnread = useMemo(() => {
    if (!latestReleaseId) return false;
    return !lastSeenReleaseId || lastSeenReleaseId < latestReleaseId;
  }, [latestReleaseId, lastSeenReleaseId]);

  const isUnread = useCallback(
    (releaseId: string) => !lastSeenReleaseId || lastSeenReleaseId < releaseId,
    [lastSeenReleaseId],
  );

  const markAllSeen = useCallback(() => {
    if (!latestReleaseId) return;
    try {
      localStorage.setItem(LAST_SEEN_KEY, latestReleaseId);
    } catch {
      //* 저장 실패해도 화면 동작은 그대로 (다음 방문에 배지가 다시 뜰 뿐)
    }
    setLastSeenReleaseId(latestReleaseId);
  }, [latestReleaseId]);

  return {
    notes,
    releaseIds,
    notesByRelease,
    latestReleaseId,
    hasUnread,
    isUnread,
    markAllSeen,
  };
};
